import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { Box, Typography } from 'seagull';
import { RichTextEditor } from 'seagull/components/RichTextEditor/RichTextEditor';
import { RichTextViewer } from 'seagull/components/RichTextViewer/RichTextViewer';
import { EMPTY_QUILL_DELTA } from 'seagull/utils/richText';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOverlayContext, useStore } from '~/contexts';
import { useSaveCustomPageText } from '~/hooks/reactQuery/mutations/useSaveCustomPageText';
import { useCustomPageText } from '~/hooks/reactQuery/queries/useCustomPageText';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
export const GetTextPageContentTID = (parentTestId)=>getTestIds(parentTestId);
const DEBOUNCE_MS = 1000;
const isQuillContentEmpty = (value)=>{
    var _value_value;
    const ops = value === null || value === void 0 ? void 0 : (_value_value = value.value) === null || _value_value === void 0 ? void 0 : _value_value.ops;
    if (ops == null || ops.length === 0) return true;
    return ops.every((op)=>typeof op.insert === 'string' && op.insert.trim() === '');
};
export const TextPageContent = (props)=>{
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetTextPageContentTID(props.testId), [
        props.testId
    ]);
    const isQueryEnabled = props.textId !== '';
    const { content: loadedContent, isLoading } = useCustomPageText({
        sourceId: isQueryEnabled ? props.getSourceId() : undefined,
        textId: props.textId,
        enabled: isQueryEnabled
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: testIds.TextPageContentTestIds.Root(),
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: testIds.TextPageContentTestIds.Loading(),
                variant: "paragraph2",
                children: t.components.loadingIndicator.loading()
            })
        });
    }
    return /*#__PURE__*/ _jsx(TextPageContentEditor, _object_spread_props(_object_spread({}, props), {
        initialContent: loadedContent !== null && loadedContent !== void 0 ? loadedContent : EMPTY_QUILL_DELTA,
        testIds: testIds
    }));
};
const TextPageContentEditor = (param)=>{
    let { textId, mintOnFirstWrite, saveFailed, getSourceId, onTextIdChange, onContentEmptyChange, onSaveFailedChange, disabled, submitAttempted, initialContent, testIds } = param;
    const { t } = useLocaleKeys();
    const businessId = useStore(selectors.user.businessId);
    const { saveText } = useSaveCustomPageText();
    const { showErrorToast } = useOverlayContext();
    const [initialValue] = useState(()=>initialContent);
    const [empty, setEmpty] = useState(()=>isQuillContentEmpty(initialContent));
    const saveQueueRef = useRef(Promise.resolve(''));
    useEffect(()=>{
        onContentEmptyChange(empty);
    }, [
        empty,
        onContentEmptyChange
    ]);
    const debouncedSave = useMemo(()=>debounce((args)=>{
            saveQueueRef.current = saveQueueRef.current.then(async (previousTextId)=>{
                const forceMint = args.mintOnFirstWrite && previousTextId === '';
                const effectiveTextId = forceMint ? '' : previousTextId !== '' ? previousTextId : args.textId;
                try {
                    const result = await args.saveText({
                        businessId: args.businessId,
                        textId: effectiveTextId,
                        sourceId: args.sourceId,
                        content: args.content
                    });
                    args.onSaveFailedChange(false);
                    if (effectiveTextId === '' && result.textId) {
                        args.onTextIdChange(result.textId);
                        return result.textId;
                    }
                    return effectiveTextId;
                } catch (unused) {
                    args.showErrorToast(args.errorMessage);
                    args.onSaveFailedChange(true);
                    return effectiveTextId;
                }
            });
        }, DEBOUNCE_MS), []);
    const handleChange = useCallback((newValue)=>{
        setEmpty(isQuillContentEmpty(newValue));
        if (disabled) return;
        const resolvedSourceId = getSourceId();
        if (resolvedSourceId === '') return;
        debouncedSave({
            content: newValue,
            textId,
            mintOnFirstWrite,
            sourceId: resolvedSourceId,
            businessId: businessId !== null && businessId !== void 0 ? businessId : '',
            saveText,
            onTextIdChange,
            onSaveFailedChange,
            showErrorToast,
            errorMessage: t.customPages.templates.saveTextError()
        });
    }, [
        businessId,
        debouncedSave,
        disabled,
        getSourceId,
        mintOnFirstWrite,
        onSaveFailedChange,
        onTextIdChange,
        saveText,
        showErrorToast,
        t,
        textId
    ]);
    useEffect(()=>()=>debouncedSave.cancel(), [
        debouncedSave
    ]);
    const showError = !disabled && !!submitAttempted && (empty || saveFailed === true);
    const helperText = showError ? empty ? t.customPages.textContent.emptyContentError() : t.customPages.textContent.saveFailedError() : undefined;
    return /*#__PURE__*/ _jsx(Box, {
        testId: testIds.TextPageContentTestIds.Root(),
        children: disabled ? /*#__PURE__*/ _jsx(RichTextViewer, {
            value: initialValue,
            testId: testIds.TextPageContentTestIds.Viewer()
        }) : /*#__PURE__*/ _jsx(RichTextEditor, {
            bi: {
                name: 'edit_custom_page_text_content'
            },
            testId: testIds.TextPageContentTestIds.Editor(),
            initialValue: initialValue,
            onChange: handleChange,
            error: showError,
            helperText: helperText,
            helperTextReserveSpace: true
        })
    });
};
